import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { ISettlementOrderQueue } from 'services/settlementOrder/types';
import { IInfinitePageParams } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IFinancesDetail,
  IGetSettlementLabelInfiniteParams,
  ISettlementLabelPageResponse,
  ISettlementLabelResponse,
  ISettlementLabelStatistics,
  ISettlementTimeline,
  ResponseHandlerResult,
} from './types';

export const useGetSettlementInfinite = (
  params: IInfinitePageParams,
  startSettlementPeriodId?: string,
  endSettlementPeriodId?: string
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ISettlementLabelResponse[], DefaultError>({
    queryKey: [
      'settlement',
      params,
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
          ...(startSettlementPeriodId && {
            StartSettlementPeriodId: startSettlementPeriodId,
          }),
          ...(endSettlementPeriodId && {
            EndSettlementPeriodId: endSettlementPeriodId,
          }),
        },
      });

      const value = await responseHandler<ISettlementLabelPageResponse>(
        response,
        {
          groupName: `useGetSettlementInfinite - ${pageParam}`,
        }
      );

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetSettlementOrderQueue = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementOrderQueue[], DefaultError>({
    queryKey: ['settlement-order-queue', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `SettlementOrder/UnderGeneration/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetSettlementOrderQueue',
        notFoundPath: '/label',
      });
    },
  });
};

export const useGetSettlementStatistics = (
  startSettlementPeriodId?: string,
  endSettlementPeriodId?: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementLabelStatistics, DefaultError>({
    queryKey: [
      'settlement-statistics',
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement`,
        params: {
          page: 0,
          itemCount: 1,
          ...(startSettlementPeriodId && {
            StartSettlementPeriodId: startSettlementPeriodId,
          }),
          ...(endSettlementPeriodId && {
            EndSettlementPeriodId: endSettlementPeriodId,
          }),
        },
      });

      const { statistics } =
        await responseHandler<ISettlementLabelPageResponse>(response, {
          groupName: `useGetSettlementStatistics`,
        });

      return statistics;
    },
  });
};

export const useGetSettlementFile = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['settlement-file', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/File/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetSettlementFilePreview = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['settlement-file-preview', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/File/${id}/Preview`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};
export const useGetSettlementFiles = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['settlement-files', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Files/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetSettlementLabelInfinite = (
  LabelId: string,
  params: IGetSettlementLabelInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ISettlementLabelResponse[], DefaultError>({
    queryKey: ['settlement-label', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Label`,
        params: { ...params, itemCount, page: pageParam, LabelId },
      });

      const value = await responseHandler<ISettlementLabelPageResponse>(
        response,
        {
          groupName: `useGetSettlementLabelInfinite - ${pageParam}`,
        }
      );

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetSettlementLabelStatistics = (
  LabelId: string,
  params: IGetSettlementLabelInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementLabelStatistics, DefaultError>({
    queryKey: ['settlement-label-statistics', params],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Label`,
        params: {
          ...params,
          LabelId,
          page: 0,
          itemCount: 1,
        },
      });

      const { statistics } =
        await responseHandler<ISettlementLabelPageResponse>(response, {
          groupName: `useGetSettlementList`,
        });

      return statistics;
    },
  });
};

export const useGetLabelBalance = (labelId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IFinancesDetail[], DefaultError>({
    queryKey: ['label-balance', labelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Finance/${labelId}`,
        params: { labelId },
      });

      const value = await responseHandler<ResponseHandlerResult>(response, {
        groupName: `useGetLabelBalance - ${labelId}`,
      });

      return value.items;
    },
  });
};

export const useGetLabelBalanceHistory = (labelId: string) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(0, 15);

  return useInfiniteQuery<IFinancesDetail[], DefaultError>({
    queryKey: ['label-balance-history', labelId],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Finance/Historical`,
        params: {
          labelId,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ResponseHandlerResult>(response, {
        groupName: `useGetLabelBalanceHistory - ${labelId}`,
      });

      return infiniteHandler<IFinancesDetail>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useDeleteSettlement = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteSettlement');

  return useMutation<boolean, DefaultError, { settlementId: string }>({
    mutationFn: async ({ settlementId }) => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('settlement.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'Settlement',
        body: { settlementId },
      });

      return responseHandler(response, {
        groupName: 'useDeleteSettlement',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('settlement.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetSettlementTimeline = (labelId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementTimeline[], DefaultError>({
    queryKey: ['settlement-timeline', labelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/Timeline`,
        params: { labelId },
      });

      return responseHandler(response, {
        groupName: 'useGetSettlementTimeline',
        notFoundPath: '/label',
      });
    },
  });
};

export const useGetSettlementRequestsCount = (enabled = true) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<number, DefaultError>({
    queryKey: ['settlement-requests-count'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Settlement/RequestsCount`,
      });

      return responseHandler(response, {
        groupName: 'useGetSettlementRequestsCount',
      });
    },
    refetchInterval: 60_000,
    enabled,
  });
};
